import React, { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  IconButton,
  Drawer,
  Button,
} from "@mui/material";
import CommonTooltip from "../../../components/CommonTooltip";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { accessMatrix, tabList } from "../../../config/roleAccessConfig";
import CloseIcon from "@mui/icons-material/Close";
import headerLogoImage from "../../../assets/image/headerLogoImage.png";

const Sidebar = forwardRef(({ activeTab, setActiveTab, userRole, onCollapseChange }, ref) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => {
    const saved = localStorage.getItem("sidebarCollapsed");
    return saved ? JSON.parse(saved) : true; // Default to collapsed
  });
  
  useImperativeHandle(ref, () => ({
    collapse: () => {
      setCollapsed(true);
    }
  }));

  useEffect(() => {
    localStorage.setItem("sidebarCollapsed", JSON.stringify(collapsed));
    if (onCollapseChange) {
      onCollapseChange(collapsed);
    }
  }, [collapsed, onCollapseChange]);
  // Build tabConfig based on accessMatrix
  const access = accessMatrix[userRole] || {};
  const tabConfig = tabList.filter((tab) => access[tab.label]);
  
  // Create a mapping from filtered index to original tab value
  const getTabIndexByValue = (value) => {
    return tabConfig.findIndex(tab => tab.value === value);
  };
  
  const getTabValueByIndex = (index) => {
    return tabConfig[index]?.value ?? 0;
  };
  
  // Convert activeTab (original value) to filtered index for Tabs component
  const activeTabIndex = getTabIndexByValue(activeTab) >= 0 ? getTabIndexByValue(activeTab) : 0;
  
  const toggleDrawer = () => {
    setDrawerOpen((prev) => !prev);
  };

  const toggleCollapse = () => {
    setCollapsed((prev) => !prev);
  };

  // Sidebar content for reuse
  const sidebarContent = (isCollapsed = false) => (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        position: "relative",
      }}
    >
      <Tabs
        orientation="vertical"
        value={activeTabIndex}
        onChange={(event, index) => {
          const originalValue = getTabValueByIndex(index);
          if (originalValue !== undefined && originalValue !== null) {
            setActiveTab(originalValue);
          setDrawerOpen(false); // close drawer on tab select
          }
        }}
        sx={{
          mt: 8,
          width: "100%",
          fontFamily: "Roboto, Arial, sans-serif",
          "& .MuiTabs-indicator": {
            display: "none",
          },
        }}
        TabIndicatorProps={{
          style: { display: "none" },
        }}
      >
        {tabConfig.map((tab, idx) => {
          const tabContent = (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                justifyContent: isCollapsed ? "center" : "flex-start",
                  fontFamily: "Roboto, Arial, sans-serif",
                width: "100%",
                transition: "justify-content 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
                }}
              >
                {tab.icon}
                <Typography
                  sx={{
                    color: activeTab === tab.value ? "#1565c0" : "#555",
                    fontWeight: 600,
                    fontSize: 15,
                    letterSpacing: 0.5,
                    textShadow:
                      activeTab === tab.value ? "0 2px 8px #e3f2fd" : "none",
                    fontFamily: "Roboto, Arial, sans-serif",
                    transition:
                    "opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1), width 0.3s cubic-bezier(0.4, 0, 0.2, 1), margin 0.3s cubic-bezier(0.4, 0, 0.2, 1), color 0.2s, font-weight 0.2s, text-shadow 0.2s",
                    whiteSpace: "nowrap",
                  ml: 1.5,
                  opacity: isCollapsed ? 0 : 1,
                  width: isCollapsed ? 0 : "auto",
                  overflow: "hidden",
                  }}
                >
                  {tab.label}
                </Typography>
              </Box>
          );

          const tabElement = (
            <Tab
              label={tabContent}
              value={idx}
            sx={{
                alignItems: "center",
                justifyContent: "center",
                px: isCollapsed ? 1 : 2,
              py: 2.2,
              borderRadius: 2.5,
                mx: isCollapsed ? 0.5 : 2,
              mb: 1.5,
                minWidth: isCollapsed ? "auto" : "unset",
                position: "relative",
                cursor: "pointer",
                pointerEvents: "auto",
                zIndex: 2,
                transition:
                  "background 0.35s cubic-bezier(.4,0,.2,1), color 0.25s, box-shadow 0.25s, font-weight 0.25s, transform 0.35s cubic-bezier(.4,0,.2,1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              background:
                activeTab === tab.value
                  ? "linear-gradient(90deg, #e3f2fd 60%, #bbdefb 100%)"
                  : "transparent",
              color: activeTab === tab.value ? "#1565c0" : "#555",
              boxShadow: activeTab === tab.value ? "0 4px 16px 0 #e3f2fd" : 0,
              fontWeight: 600,
              fontFamily: "Roboto, Arial, sans-serif",
                "&::before": {
                  content: activeTab === tab.value ? '""' : "none",
                  position: "absolute",
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 4,
                  backgroundColor: "#1976d2",
                  borderRadius: 2,
                  zIndex: 1,
                },
              "&:hover": {
                background: "linear-gradient(90deg, #e3f2fd 60%, #bbdefb 100%)",
                color: "#1976d2",
                fontWeight: 600,
                transform: "scale(1.04)",
                boxShadow: "0 6px 20px 0 #e3f2fd",
              },
            }}
          />
          );

          // Only wrap with Tooltip when collapsed, otherwise render Tab directly
          if (isCollapsed) {
            return (
              <CommonTooltip title={tab.label} placement="right" key={tab.label}>
                {tabElement}
              </CommonTooltip>
            );
          }
          
          return tabElement;
        })}
      </Tabs>
    </Box>
  );

  return (
    <>
      <Box
        sx={{
          display: {
            xs: "block",
            md: "none",
            fold: "block",
            "853_1280": "block",
            "912_1368": "block",
          },
          position: "fixed",
          top: 16,
          left: 20,
          zIndex: 1201,
        }}
      >
        <IconButton 
          onClick={toggleDrawer}
          size="small"
          sx={{
            padding: "4px",
            "& .MuiSvgIcon-root": {
              fontSize: "1.5rem", // Smaller icon size
            },
          }}
        >
          {drawerOpen ? (
            <CloseIcon />
          ) : (
            <MenuIcon />
          )}
        </IconButton>
      </Box>

      <Box
        data-sidebar
        sx={{
          width: collapsed ? {md: "80px", lg: "80px"} : {md:"17%",lg:"13%"},
          bgcolor: "#fffbf0",
          borderRight: "none",
          minHeight: "calc(100vh - 80px)",
          position: "fixed",
          left: 0,
          top: "80px",
          display: {
            xs: "none",
            md: "flex",
            fold: "none",
            "853_1280": "none",
            "912_1368": "none",
          },
          flexDirection: "column",
          alignItems: "center",
          boxShadow: "none",
          zIndex: 1000,
          fontFamily: "Roboto, Arial, sans-serif",
          transition: "width 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1), margin 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        <IconButton
          data-sidebar-toggle
          onClick={toggleCollapse}
          sx={{
            position: "absolute",
            top: 16,
            right: collapsed ? "50%" : 8,
            transform: collapsed ? "translateX(50%)" : "none",
            zIndex: 11,
            bgcolor: "#FFC71A",
            "&:hover": {
              bgcolor: "#FFD54F",
            },
            transition: "all 0.3s ease",
          }}
        >
          {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
        </IconButton>
        {sidebarContent(collapsed)}
      </Box>
      {/* Drawer for mobile, foldables, 853x1280, and 912x1368 */}
      <Drawer
        anchor="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        sx={{
          display: {
            xs: "block",
            md: "none",
            fold: "block",
            "853_1280": "block",
            "912_1368": "block",
          },
        }}
        PaperProps={{
          sx: {
            width: 220,
            background: "#fffbf0",
            fontFamily: "Roboto, Arial, sans-serif",
            pt: 2,
          },
        }}
      >
        {sidebarContent(false)}
      </Drawer>
    </>
  );
});

Sidebar.displayName = 'Sidebar';

export default Sidebar;
